import { reactive, ref } from 'vue';
import { fetchWrapper } from '../utils/FetchWrapper';

export interface PaginationConfig {
    apiEndpoint: string;
    buildApiParams: () => any;
    initialPageSize?: number;
    pageSizeOptions?: number[];
}

export interface PaginationState {
    currentPage: number;
    pageSize: number;
    pageSizeOptions: number[];
    hasNext: boolean;
    isLoading: boolean;
    loadedPages: number[];
    isInitialLoad: boolean;
}

export function usePagination<T = any>(config: PaginationConfig) {
    const {
        apiEndpoint,
        buildApiParams,
        initialPageSize = 10,
        pageSizeOptions = [10, 20, 50, 100, 500, 1000]
    } = config;

    // Reactive state
    const pagination = reactive<PaginationState>({
        currentPage: 1,
        pageSize: initialPageSize,
        pageSizeOptions,
        hasNext: false,
        isLoading: false,
        loadedPages: [],
        isInitialLoad: true
    });

    // Cursor-based storage: array index = page number - 1
    const pagesData = ref<T[][]>([]);
    const cursorStack = ref<(string | null)[]>([null]); // null = first page (no cursor)
    const currentPageData = ref<T[]>([]);
    const errorMessage = ref<string>('');

    // API call function for cursor-based pagination
    const getApiData = async (params: any): Promise<{ data: T[]; pagination: { nextCursor?: string } } | null> => {
        try {
            const queryParams = new URLSearchParams();

            Object.keys(params).forEach((key) => {
                if (params[key] !== undefined && params[key] !== null) {
                    queryParams.append(key, params[key].toString());
                }
            });

            const requestUrl = `${apiEndpoint}?${queryParams.toString()}`;
            const response = await fetchWrapper.get(requestUrl, '');

            if (response.success) {
                // Handle different API response formats
                if (response.result && Array.isArray(response.result)) {
                    // Legacy APIs that return direct array (Payment, Refund, etc.)
                    return {
                        data: response.result,
                        pagination: {} // No pagination for legacy APIs
                    };
                } else if (response.result && response.result.data) {
                    // New cursor-based pagination APIs
                    return {
                        data: response.result.data,
                        pagination: {
                            nextCursor: response.result.pagination?.nextCursor
                        }
                    };
                }
                return { data: [], pagination: {} };
            }

            errorMessage.value = response.errors || 'Failed to fetch data';
            return null;
        } catch (error) {
            errorMessage.value = 'An unexpected error occurred while fetching data';
            console.error('API Error:', error);
            return null;
        }
    };

    // Core pagination methods
    const loadPage = async (page: number, resetData: boolean = false): Promise<void> => {
        const pageIndex = page - 1; // Convert to 0-based index

        // Check if page is already loaded (unless it's a reset)
        if (!resetData && pagesData.value[pageIndex]) {
            switchToPage(page);
            return;
        }

        pagination.isLoading = true;

        try {
            const params = buildApiParams();
            params.limit = pagination.pageSize;

            // Add cursor for pages after the first
            const cursor = cursorStack.value[pageIndex];
            if (cursor) {
                params.cursor = cursor;
            }

            const result = await getApiData(params);

            if (result) {
                // Reset all data if needed (filter change or initial load)
                if (resetData) {
                    pagesData.value = [];
                    cursorStack.value = [null];
                    pagination.loadedPages = [];
                }

                // Store page data at correct index
                const pages = [...pagesData.value] as T[][];
                pages[pageIndex] = result.data;
                pagesData.value = pages;

                // Update loaded pages list for dropdown
                if (!pagination.loadedPages.includes(page)) {
                    pagination.loadedPages.push(page);
                    pagination.loadedPages.sort((a, b) => a - b); // Keep sorted
                }

                // Update pagination state
                pagination.currentPage = page;
                pagination.hasNext = !!result.pagination.nextCursor;
                pagination.isInitialLoad = false;

                // Store next cursor if available
                if (result.pagination.nextCursor) {
                    cursorStack.value[page] = result.pagination.nextCursor;
                }

                // Set current page data
                currentPageData.value = result.data;
            }
        } finally {
            pagination.isLoading = false;
        }
    };

    const switchToPage = (page: number): void => {
        const pageIndex = page - 1;
        if (pagesData.value[pageIndex]) {
            pagination.currentPage = page;
            currentPageData.value = pagesData.value[pageIndex];
        }
    };

    const resetPagination = (): void => {
        pagination.currentPage = 1;
        pagination.hasNext = false;
        pagination.loadedPages = [];
        pagination.isInitialLoad = true;
        pagesData.value = [];
        cursorStack.value = [null];
        currentPageData.value = [];
    };

    // Navigation methods
    const nextPage = async (): Promise<void> => {
        if (pagination.hasNext && !pagination.isLoading) {
            await loadPage(pagination.currentPage + 1);
        }
    };

    const previousPage = (): void => {
        if (pagination.currentPage > 1) {
            const prevPage = pagination.currentPage - 1;
            // Previous page should already be loaded, so just switch to it
            switchToPage(prevPage);
        }
    };

    const selectPage = (selectedPage: number): void => {
        if (selectedPage) {
            // Page should already be loaded (dropdown only shows loaded pages)
            switchToPage(selectedPage);
        }
    };

    const changePageSize = async (): Promise<void> => {
        // Reset pagination when page size changes (cursors are invalidated)
        resetPagination();
        await loadPage(1, true);
    };

    // Utility method to update item in all data structures
    const updateItemInAllPages = (itemId: string | number, idField: string, propertyName: string, value: any): void => {
        // Update current page data
        const currentPageItem: any = currentPageData.value.find((item: any) => item[idField] === itemId);
        if (currentPageItem) {
            currentPageItem[propertyName] = value;
        }

        // Update stored page data
        const pageIndex = pagination.currentPage - 1;
        if (pagesData.value[pageIndex]) {
            const pageData = pagesData.value[pageIndex];
            const storedItem: any = pageData.find((item: any) => item[idField] === itemId);
            if (storedItem) {
                storedItem[propertyName] = value;
            }
        }
    };

    // Utility method to remove item from all data structures
    const removeItemFromAllPages = (itemId: string | number, idField: string): void => {
        // Remove from current page data
        currentPageData.value = currentPageData.value.filter((item: any) => item[idField] !== itemId);

        // Update the stored page data
        const pageIndex = pagination.currentPage - 1;
        if (pagesData.value[pageIndex]) {
            const pages = [...pagesData.value] as T[][];
            pages[pageIndex] = pages[pageIndex].filter((item: any) => item[idField] !== itemId);
            pagesData.value = pages;
        }
    };

    // Initialize - load first page
    const initialize = async (): Promise<void> => {
        await loadPage(1, true);
    };

    return {
        // State
        pagination,
        currentPageData,
        errorMessage,

        // Methods
        loadPage,
        switchToPage,
        resetPagination,
        nextPage,
        previousPage,
        selectPage,
        changePageSize,
        updateItemInAllPages,
        removeItemFromAllPages,
        initialize
    };
}
